// ----------------------------------------------------------------------------
// Invite codes, one per team. People enter a code after signing up and an
// admin approves them from Requests. Codes only: invite links and invite by
// email are retired (see DEPRECATED.md).
// ----------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { groupsApi } from '@app/api/organization';
import { Toast } from '@app/components/Notifications';
import { Button, Card, ConfirmDialog, Field, PageHeader, Segmented, Select, Toggle, cx } from '@app/components/ui';
import { monthDayYear } from '@app/lib/format';
import { redesignFlags } from '@app/featureFlags';
import { logger } from '@app/utils/logger';

export const InviteCodes: React.FC = () => {
  const { groups, refetchGroups, updateGroupInviteCode } = useGroup();
  const [teamId, setTeamId] = useState('');
  const [days, setDays] = useState<'1' | '7' | '30'>('7');
  const [busy, setBusy] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [revoke, setRevoke] = useState<{ id: string; code: string; name: string } | null>(null);
  const [toast, setToast] = useState<{ m: string; t: 'success' | 'error' } | null>(null);

  useEffect(() => {
    void refetchGroups();
  }, [refetchGroups]);
  useEffect(() => {
    if (!teamId && groups[0]) setTeamId(groups[0].id);
  }, [groups, teamId]);

  const withCode = groups.filter((g) => g.inviteCode && (!g.inviteCodeExpiry || new Date(g.inviteCodeExpiry) > new Date()));

  const generate = async (groupId: string) => {
    setBusy(groupId);
    try {
      const result = await groupsApi.generateInviteCode(groupId, Number(days));
      updateGroupInviteCode(groupId, result.inviteCode, result.expiresAt);
      setFresh(result.inviteCode);
      setToast({ m: `Code ${result.inviteCode} created`, t: 'success' });
      void refetchGroups();
    } catch (err: any) {
      logger.error('Failed to generate invite code', err);
      setToast({ m: err?.response?.data?.error || 'Could not generate a code', t: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const doRevoke = async () => {
    if (!revoke) return;
    try {
      await groupsApi.revokeInviteCode(revoke.id);
      updateGroupInviteCode(revoke.id, null, null);
      setToast({ m: `Code ${revoke.code} revoked`, t: 'success' });
      void refetchGroups();
    } catch {
      setToast({ m: 'Could not revoke the code', t: 'error' });
    }
  };

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setToast({ m: `Code ${code} copied`, t: 'success' });
    } catch {
      setToast({ m: 'Copy failed. Select the code and copy it manually.', t: 'error' });
    }
  };

  return (
    <div className="max-w-[820px]">
      {toast && <Toast message={toast.m} type={toast.t} onClose={() => setToast(null)} />}
      <PageHeader
        eyebrow="People"
        title="Invite codes"
        description="Share a code with someone. They enter it after signing up, and you approve them from Requests."
      />

      <Card>
        <h2 className="text-[15px] font-semibold mb-3.5">Generate a code</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <Field label="Joins team" htmlFor="code-team" className="flex-[1_1_220px]">
            <Select id="code-team" value={teamId} onChange={setTeamId} options={groups.map((g) => ({ value: g.id, label: g.name, sub: g.inviteCode ? `Current code ${g.inviteCode}` : 'No code yet' }))} />
          </Field>
          <div>
            <span className="wp-label" id="code-days">
              Expires after
            </span>
            <Segmented
              label="Expires after"
              value={days}
              onChange={setDays}
              className="!h-[46px] items-center"
              options={[
                { value: '1', label: '1 day' },
                { value: '7', label: '7 days' },
                { value: '30', label: '30 days' },
              ]}
            />
          </div>
          <Button shape="form" size="lg" className="!rounded-[12px]" loading={busy === teamId} disabled={!teamId} onClick={() => generate(teamId)}>
            Generate code
          </Button>
        </div>
        <p className="text-[12.5px] text-t4 mt-3">Generating a new code for a team replaces its current one.</p>
      </Card>

      <h2 className="text-[13px] font-semibold text-t3 mt-[22px] mb-2.5">Active codes</h2>
      {withCode.length === 0 ? (
        <div className="p-[30px] text-center text-t4 text-[14px] rounded-[18px] bg-fill">No active codes.</div>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {withCode.map((g) => {
            const isFresh = fresh === g.inviteCode;
            return (
              <li
                key={g.id}
                className={cx('flex flex-wrap items-center gap-4 rounded-[20px] border px-5 py-4', isFresh ? 'bg-acc-bg border-[rgba(208,48,69,.35)]' : 'bg-card border-line')}
                style={{ transition: 'background .3s ease' }}
              >
                <span className="font-mono text-[20px] font-bold tracking-[.08em]">{g.inviteCode}</span>
                <div className="flex-1 min-w-[180px] text-[13px] text-t3">
                  {g.name}
                  <span className="block text-[12px] text-t4 mt-0.5">{g.inviteCodeExpiry ? `Expires ${monthDayYear(g.inviteCodeExpiry)}` : 'No expiry'}</span>
                </div>
                {redesignFlags.inviteCodeToggle && <Toggle checked label={`${g.name} code enabled`} onChange={() => undefined} />}
                <Button variant="dark" size="sm" className="!h-[34px] !px-3.5 !text-[13px]" onClick={() => copy(g.inviteCode!)}>
                  Copy
                </Button>
                <Button variant="ghost" size="sm" className="!h-[34px] !px-3.5 !text-[13px]" loading={busy === g.id} onClick={() => generate(g.id)}>
                  Regenerate
                </Button>
                <Button variant="danger-ghost" size="sm" className="!h-[34px] !px-3.5 !text-[13px]" onClick={() => setRevoke({ id: g.id, code: g.inviteCode!, name: g.name })}>
                  Revoke
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        isOpen={!!revoke}
        onClose={() => setRevoke(null)}
        onConfirm={doRevoke}
        type="danger"
        title={revoke ? `Revoke ${revoke.code}?` : 'Revoke code?'}
        message={revoke ? `People can no longer use it to join ${revoke.name}. Anyone who already asked to join stays in Requests.` : ''}
        confirmText="Revoke code"
      />
    </div>
  );
};
